import { Stack } from '@/design-system/stack/stack';
import { AccountInvestmentTypeView } from '@/types/entity-types';
import { exhaustiveCheck } from '@/utils/exhaustive-check';
import { ComponentProps, ReactNode } from 'react';
import { KiidAcceptanceStep } from './components/kiid-acceptance-step';
import { EuWaitList } from './components/onboarding/eu-wait-list';
import { VerificationPending } from './components/onboarding/verification-pending';
import { VerificationRequired } from './components/onboarding/verification-required';
import { TreasuryDashboard } from './components/treasury-dashboard';
import { HoldingDetails } from './types/treasury-types';
import { ReturnData } from './utils/get-return-data';

export type TreasuryStep =
  | 'EMPTY_STATE_EU_WAIT_LIST'
  | 'EMPTY_STATE_NO_KIID'
  | 'EMPTY_STATE_NO_ACCOUNT'
  | 'EMPTY_STATE_PENDING'
  | 'APPROVED';

type TreasuryFlowConfig =
  | {
      step: Extract<TreasuryStep, 'EMPTY_STATE_PENDING'>;
    }
  | {
      step: Extract<TreasuryStep, 'EMPTY_STATE_EU_WAIT_LIST'>;
      hasRecordedInterest: boolean;
    }
  | {
      step: Extract<TreasuryStep, 'EMPTY_STATE_NO_KIID'>;
      holdingDetails: HoldingDetails;
      companyId: string;
      isin: string;
      onContinue: () => void;
    }
  | {
      step: Extract<TreasuryStep, 'EMPTY_STATE_NO_ACCOUNT'>;
      onBookCall: () => void;
    }
  | {
      step: Extract<TreasuryStep, 'APPROVED'>;
      account: AccountInvestmentTypeView;
      holdingDetails: HoldingDetails;
      returnData: ReturnData;
      onInitiateDeposit: ComponentProps<typeof TreasuryDashboard>['onInitiateDeposit'];
      onInitiateWithdrawal: ComponentProps<typeof TreasuryDashboard>['onInitiateWithdrawal'];
    };

type TreasuryFlowProps = {
  treasuryFlowConfig: TreasuryFlowConfig;
};

export const TreasuryFlow = ({ treasuryFlowConfig }: TreasuryFlowProps) => {
  const { step } = treasuryFlowConfig;

  switch (step) {
    case 'EMPTY_STATE_EU_WAIT_LIST': {
      const { hasRecordedInterest } = treasuryFlowConfig;

      return (
        <StepWrapper>
          <EuWaitList hasRecordedInterest={hasRecordedInterest} />
        </StepWrapper>
      );
    }
    case 'EMPTY_STATE_NO_KIID': {
      const { holdingDetails, companyId, isin, onContinue } = treasuryFlowConfig;

      return (
        <StepWrapper>
          <KiidAcceptanceStep
            stepConfig={{
              variant: 'EMPTY_STATE_NO_KIID',
              holdingDetails,
              companyId,
              isin,
              onContinue,
            }}
          />
        </StepWrapper>
      );
    }
    case 'EMPTY_STATE_NO_ACCOUNT': {
      const { onBookCall } = treasuryFlowConfig;

      return (
        <StepWrapper>
          <VerificationRequired onBookACall={onBookCall} />
        </StepWrapper>
      );
    }
    case 'EMPTY_STATE_PENDING':
      return (
        <StepWrapper>
          <VerificationPending />
        </StepWrapper>
      );
    case 'APPROVED': {
      const { account, holdingDetails, returnData, onInitiateDeposit, onInitiateWithdrawal } = treasuryFlowConfig;

      return (
        <TreasuryDashboard
          account={account}
          holdingDetails={holdingDetails}
          returnData={returnData}
          onInitiateDeposit={onInitiateDeposit}
          onInitiateWithdrawal={onInitiateWithdrawal}
        />
      );
    }
    default:
      return exhaustiveCheck(step);
  }
};

type StepWrapperProps = {
  children: ReactNode;
};

const StepWrapper = ({ children }: StepWrapperProps) => {
  return (
    <Stack justify="center" className="mx-auto max-w-[512px] flex-1 overflow-y-auto pb-10">
      {children}
    </Stack>
  );
};
